import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import AdherenceCard from './OverviewTab/AdherenceCard';
import IssuesCard from './OverviewTab/IssuesCard';
import CSATCard from './OverviewTab/CSATCard';
import DWSCard from './OverviewTab/DWSCard';
import VitalsCard from './OverviewTab/VitalsCard';
import DetailsPanel from './OverviewTab/DetailsPanels/DetailsPanel';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOwnerOverview } from '../../../../store/hubOwner/actionCreators';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import { SurveyType } from '../../../../shared/constants';
import { getLatestSurveyResponse } from '../../../../shared/surveys';
import type { OwnerOverview } from '../../../../store/hubOwner/types';

interface OverviewTabProps {
  ownerId: number;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ ownerId }) => {
  const dispatch = useDispatch();

  const [selectedCard, setSelectedCard] = useState<string | null>(null);

  const { ownerOverview, ownerOverviewLoading } = useSelector(
    (state: any) => state.hubOwner,
  ) as {
    ownerOverview: OwnerOverview | null | undefined;
    ownerOverviewLoading: boolean;
  };

  useEffect(() => {
    if (!ownerOverview || ownerOverview.ownerId !== ownerId) {
      dispatch(fetchOwnerOverview(ownerId));
    }
  }, [dispatch, ownerId, ownerOverview]);

  const adherenceData = {
    adherent: ownerOverview?.adherence_count?.adherent ?? 0,
    nonAdherent: ownerOverview?.adherence_count?.['non-adherent'] ?? 0,
    unknown: ownerOverview?.adherence_count?.unknown ?? 0,
    total: ownerOverview?.adherence_count?.total ?? 0,
  };

  // For issues,
  const issuesData = {
    high: ownerOverview?.active_issue?.high ?? 0,
    low: ownerOverview?.active_issue?.low ?? 0,
  };

  // Latest response per survey type (OI-1769). The scalar fields are kept as
  // fallbacks for backends that do not return `survey_responses` yet.
  const latestCsat = getLatestSurveyResponse(
    ownerOverview,
    SurveyType.HubOverallExperience,
  );
  const latestWellness = getLatestSurveyResponse(
    ownerOverview,
    SurveyType.HubDailyWellnessCheck,
  );

  // For CSAT, only last response is available,
  const csatData = {
    lastResponse:
      latestCsat?.label ?? ownerOverview?.last_survey_response ?? 'N/A',
    title: latestCsat?.title,
  };

  // For DWS (daily wellness survey), only the latest response is available.
  const dwsData = {
    lastResponse:
      latestWellness?.score ?? ownerOverview?.last_wellness_score ?? null,
    title: latestWellness?.title,
  };

  // For now Vitals always N/A
  const vitalsData = {
    HR: 'N/A',
    SpO2: 'N/A',
    BT: 'N/A',
    WT: 'N/A',
    BP: 'N/A',
  };

  if (ownerOverviewLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(auto-fill, minmax(300px, 1fr))',
            sm: 'repeat(auto-fill, minmax(300px, 1fr))',
            md: 'repeat(auto-fit, minmax(300px, 1fr))',
            lg: 'repeat(auto-fit, minmax(300px, 1fr))',
            xl: 'repeat(auto-fit, minmax(300px, 1fr))',
          },
          gap: 3,
        }}
        px={2}
        pt={2}
      >
        <AdherenceCard
          data={adherenceData}
          isSelected={selectedCard === 'adherence'}
          onClick={() => setSelectedCard('adherence')}
        />
        <IssuesCard
          data={issuesData}
          isSelected={selectedCard === 'issues'}
          onClick={() => setSelectedCard('issues')}
        />
        <CSATCard
          data={csatData}
          isSelected={selectedCard === 'csat'}
          onClick={() => setSelectedCard('csat')}
        />
        <DWSCard
          data={dwsData}
          isSelected={selectedCard === 'dws'}
          onClick={() => setSelectedCard('dws')}
        />
        {/* Fifth card wraps to the second row, under Adherence. */}
        <VitalsCard
          data={vitalsData}
          isSelected={selectedCard === 'vitals'}
          onClick={() => setSelectedCard('vitals')}
        />
      </Box>
      <Box>
        <DetailsPanel selectedCard={selectedCard} ownerId={ownerId} />
      </Box>
    </Box>
  );
};

export default OverviewTab;
